import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { CheckIcon } from '@heroicons/react/24/outline';
import * as React from 'react';
import { useChat, useIsChatLocked } from '../../../../../libs/hooks';
import { IAppConfigResult, useAppConfig } from '../../../../../libs/hooks/useAppConfig'; // Verify this path or ensure the module exists
import { AlertType } from '../../../../../libs/models/AlertType';
import { ISiteInfo } from '../../../../../libs/models/SelectedSites';
import { IAppDropdownConfigValue } from '../../../../../libs/services/AppConfigService';
import { useAppDispatch, useAppSelector } from '../../../../../redux/app/hooks';
import { RootState } from '../../../../../redux/app/store';
import { addAlert, setNotifications } from '../../../../../redux/features/app/appSlice';
import { ChatState } from '../../../../../redux/features/conversations/ChatState';
import {
    editConversationBotProfilePicture,
    editConversationScope,
} from '../../../../../redux/features/conversations/conversationsSlice';
import { getScopeIcon } from '../../../../utils/IconUtils';
import { ScopeInfoDialog } from '../../Dialogs/ScopeInfoDialog';
import { NOTIFICATION_TEMPLATES } from '../../Notifications/NotificationConstants';
// import { ScopeInfoDialog } from '../scope-dialog/ScopeInfoDialog';

interface IEditChatScopeProps {
    chatId: string;
    chatScope: string | undefined;
}

export const EditChatScope: React.FC<IEditChatScopeProps> = (props) => {
    const [scopeOptions, setScopeOptions] = React.useState<IAppDropdownConfigValue[]>();
    const dispatch = useAppDispatch();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const chat = useChat();
    const { isLockedChat } = useIsChatLocked(conversations, selectedId);
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);
    const emailAddress = activeUserInfo?.email ?? '';
    const user = emailAddress;

    const appConfig = useAppConfig();

    const getPromptGuidanceContent = async () => {
        // Fetch dropdown values for default scopes.
        const responsedropdown: IAppConfigResult<IAppDropdownConfigValue[]> =
            await appConfig.getConfigDropdownValues(user);

        if (responsedropdown.Success && responsedropdown.Data) {
            setScopeOptions(responsedropdown.Data);
        }
    };

    React.useEffect(() => {
        getPromptGuidanceContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onOptionSelect = (value: string, label: string) => {
        if (value && value !== props.chatScope) {
            onScopeChange(value, label).catch((e: any) => {
                const errorMessage = `Unable to change chat scope. Details: ${
                    e instanceof Error ? e.message : String(e)
                }`;
                dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
            });
        }
    };

    const onScopeChange = async (newScope: string, scopeLabel: string) => {
        const chatState: ChatState = conversations[selectedId];
        const selectedSites = chatState.selectedSites ?? ([] as ISiteInfo[]);
        if (chatState.isSaved) {
            await chat.editChat(
                chatState.id,
                chatState.title,
                chatState.memoryBalance,
                newScope,
                selectedSites,
                chatState.hidden,
                chatState.authoritiveDocuments,
            );
        }

        localStorage.setItem('lastSelectedChatScope', newScope.toString());
        dispatch(editConversationScope({ id: props.chatId, newScope }));
        dispatch(
            editConversationBotProfilePicture({
                id: props.chatId,
                newProfilePicture: getScopeIcon(newScope),
            }),
        );
        dispatch(
            setNotifications([
                {
                    ...NOTIFICATION_TEMPLATES.CHANGE_SOURCE,
                    description: `Source has been changed to <strong>${scopeLabel}</strong>`,
                },
            ]),
        );
    };

    const scopeTitleByName = (name: string | undefined) => {
        if (!name || !scopeOptions) {
            return import.meta.env.VITE_APP_BOT_NAME;
        }
        const scopeOption = scopeOptions.find((scope) => scope.name === name);
        return scopeOption?.title ?? import.meta.env.VITE_APP_BOT_NAME;
    };

    return scopeOptions ? (
        <div className="text-right">
            {!isLockedChat && !conversations[selectedId].hidden && <ScopeInfoDialog scopeOptions={scopeOptions} />}
            <span
                className={`mr-3 hidden md:inline-block ${isLockedChat || conversations[selectedId].hidden ? 'text-slate-500' : ''}`}>
                Choose a source:
            </span>
            <Menu as="div" className="relative inline-block text-left">
                <MenuButton
                    disabled={isLockedChat || conversations[selectedId].hidden}
                    className={`inline-flex w-48 sm:w-56 gap-x-1.5 justify-between rounded-md px-3 py-1 shadow-sm ring-1 ring-inset ${isLockedChat || conversations[selectedId].hidden ? 'ring-gray-200 bg-gray-200 hover:bg-gray-200 dark:bg-slate-700 dark:ring-slate-700 dark:hover:bg-slate-700 text-slate-500' : 'ring-gray-300 bg-white hover:bg-gray-50 dark:bg-slate-700 dark:ring-slate-600 dark:hover:bg-slate-600'}`}>
                    <span className="overflow-hidden overflow-ellipsis whitespace-nowrap">
                        {scopeTitleByName(props.chatScope)}
                    </span>
                    <ChevronDownIcon aria-hidden="true" className="-mr-1 size-5" />
                </MenuButton>

                <MenuItems
                    anchor="bottom"
                    className="absolute right-0 z-10 mt-1 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-sm">
                    {scopeOptions.map((scope) => (
                        <MenuItem key={scope.name}>
                            <button
                                onClick={() => onOptionSelect(scope.name, scope.title)}
                                className="items-center flex flex-row w-full text-left px-3 py-2 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none dark:data-[focus]:text-slate-200 dark:data-[focus]:bg-slate-600 cursor-pointer">
                                {props.chatScope === scope.name ? (
                                    <CheckIcon className="h-4 w-4 inline-block mr-2" />
                                ) : (
                                    <span className="h-4 w-4 inline-block mr-2" />
                                )}
                                <span>{scope.title}</span>
                            </button>
                        </MenuItem>
                    ))}
                </MenuItems>
            </Menu>
        </div>
    ) : null;
};
